import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Label,
  InputText,
  InputTextarea,
  TEOLoader,
  RadioGroupTab,
  InputSelect,
  Button,
  Multiselect,
} from '@teo/components';
import { Tag, CrossFilled } from '@teo/components/icons';
import UploadMainImage from '@/components/UploadMainImage/UploadMainImage';
import { axios } from '@/axios';
import { isEmpty } from '@/util';
import { config } from '@/config';
import { categoriesIcon } from '../util';
import { useMediaQuery } from 'react-responsive';
import { langData } from '@/mocks/landData';
import { PaginationMultiselectWithTags } from '@/components/PaginationMultiselectWithTags';
import { useGet } from '@/query/fetchHooks';
import i18n from 'i18next';
import AddCompetenceLP from '@/components/Competencies/AddCompetenceLP';
import { getTagsFromLearningPath } from '@/components/AiTags/util';
import { AITags } from '@/components/LibraryModal/AITag';

const HomeSettings = ({
  learningpathId,
  learningpath,
  setLearningpath,
  setNodes,
  nodes,
  setHomeImage,
  homeImage,
  categories,
  allCategories,
  isOwner,
  auth,
  authLang,
  setNewLpCompetencies,
  newLpCompetencies,
  setLpCompetencies,
  lpCompetencies,
  refetchCompetencies,
}) => {
  const { t } = useTranslation();
  const [homeName, setHomeName] = useState(learningpath?.lpName || '');
  const [homeDescription, setHomeDescription] = useState(
    learningpath?.lpDescription || ''
  );
  const [selectLang, setSelectLang] = useState('nl');
  const [newLangData, setNewLangData] = useState([]);

  const [loadingImgSpinner, setLoadingImgSpinner] = useState(false);
  const [lpCategory, setLpCategory] = useState(null);
  const [typeOfOrder, setTypeOfOrder] = useState(
    learningpath?.order || 'fixed'
  );

  const [filter, setFilter] = useState('');

  const [addCompetency, setAddCompetency] = useState(false);

  const [aiTags, setAiTags] = useState([]);
  const [aiTagsLoading, setAiTagsLoading] = useState(false);
  const [isloadedAITags, setIsloadedAITags] = useState(false);
  const [stopLoadedAITags, setStoploadedAITags] = useState(false);

  const oldTags =
    learningpath?.tags?.split(';;')?.filter((x) => {
      return x.startsWith('tag:');
    }) || [];

  const nonTags =
    learningpath?.tags?.split(';;')?.filter((x) => {
      return !x.startsWith('tag:');
    }) || [];

  const [selectTags, setSelectTags] = useState(
    oldTags?.map((x) => ({
      key: x.slice(4),
      label: x.slice(4),
      tag: x.slice(4),
    })) || []
  );

  const isMobile = useMediaQuery({
    query: '(max-width: 767px)',
  });

  useEffect(() => {
    const sortLangData = langData
      ?.map((el) => ({
        ...el,
        name: t(`langs.${el?.value}`),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
    setSelectLang(
      sortLangData?.find((option) => option?.value === learningpath?.lang) ||
        'nl'
    );
    setNewLangData(sortLangData);
  }, [langData]);

  useEffect(() => {
    if (learningpath?.tags && allCategories) {
      const learningpathTags = allCategories?.find(
        (el) => el?.label === nonTags[0]?.split(':')[1]
      );
      setLpCategory(learningpathTags);
    }
  }, [allCategories]);

  useEffect(() => {
    if (stopLoadedAITags) return;
    if (nodes.length > 1) {
      const lpContent = nodes
        ?.filter((el) => el?.data?.type != 'homepage')
        ?.map((el) => ({ type: el?.data?.type, name: el?.data?.title }));
      if (lpContent?.length > 0) {
        fetchAITags(lpContent);
      }
    }
  }, [isloadedAITags, nodes]);

  const fetchAITags = async (list) => {
    if (!isloadedAITags) return;

    setAiTagsLoading(true);

    const currentExistingTags = selectTags
      .map((el) => el?.label || el?.tag || el)
      .filter(Boolean);

    const authId = auth?.user?.id;
    const result = await getTagsFromLearningPath(
      homeName,
      list,
      currentExistingTags,
      authId
    );

    setAiTags(result);
    setAiTagsLoading(false);
    setStoploadedAITags(true);
  };

  const save = async () => {
    const changes = {};
    if (homeName !== learningpath?.name) {
      changes.lpName = homeName;
    }
    if (homeDescription !== learningpath?.description) {
      changes.lpDescription = homeDescription;
    }
    const promises = [];
    if (!isEmpty(changes)) {
      promises.push(
        axios.put(`${config.backend}/learningpath/${learningpathId}`, changes)
      );
    }
  };

  const changeName = (value) => {
    setHomeName(value);
    setLearningpath((prev) => ({ ...prev, lpName: value }));
    setNodes(
      nodes?.map((el) =>
        el?.data?.type === 'homepage'
          ? { ...el, data: { ...el.data, title: value } }
          : el
      )
    );
  };

  const changeDescription = (value) => {
    setHomeDescription(value);
    setLearningpath((prev) => ({ ...prev, lpDescription: value }));
    setNodes(
      nodes?.map((el) =>
        el?.data?.type === 'homepage'
          ? { ...el, data: { ...el.data, description: value } }
          : el
      )
    );
  };

  const changeImage = (value) => {
    setHomeImage(value);
    setNodes(
      nodes?.map((el) =>
        el?.data?.type === 'homepage'
          ? { ...el, data: { ...el.data, image: value } }
          : el
      )
    );
    axios.put(`${config.backend}/learningpath/${learningpathId}`, {
      lpPicture: value,
    });
  };

  const handleCategory = (value) => {
    setLpCategory(value);

    let lpTag = '';
    selectTags?.map((tag) => {
      lpTag = lpTag + 'tag:' + tag?.tag + ';;';
    });

    const lpCat = 'category:' + value?.label;
    const lpTags = lpTag + lpCat;
    setLearningpath((prev) => ({ ...prev, tags: lpTags }));
    axios.put(`${config.backend}/learningpath/${learningpathId}`, {
      tags: lpTags,
    });
  };

  const handleActivate = async () => {
    setLearningpath((prev) => ({
      ...prev,
      lpPublicationStatus: 'concept',
    }));
    await axios.put(`${config.backend}/learningpath/${learningpathId}`, {
      lpPublicationStatus: 'concept',
    });
  };

  const handleAddTag = () => {
    if (filter) {
      axios.post(`${config.backend}/tags`, {
        tag: filter,
        label: filter,
        key: Math.floor(Math.random() * 9999999999999),
        organisationId: auth?.user?.activeOrganisationId,
        lang: auth?.user?.lang,
      });
      setSelectTags((prev) => [
        ...prev,
        {
          key: filter,
          label: filter,
          tag: filter,
        },
      ]);
      setFilter('');
    }
  };

  useEffect(() => {
    if (selectTags) {
      handleTag(selectTags);
    }
  }, [selectTags]);

  const handleTag = (value) => {
    const newTags = value.map((x) => 'tag:' + x.label);

    if (newTags.join(';;') !== oldTags?.join(';;')) {
      let newTag = '';
      value?.map((tag) => {
        newTag = newTag + 'tag:' + tag?.tag + ';;';
      });
      const lpCat = lpCategory?.label ? 'category:' + lpCategory?.label : '';
      const lpTags = newTag + lpCat;
      setLearningpath((prev) => ({ ...prev, tags: lpTags }));
      axios.put(`${config.backend}/learningpath/${learningpathId}`, {
        tags: lpTags,
      });
    }
  };

  const hendleDeleteComp = async (item) => {
    try {
      await axios.delete(`${config.backend}/learningpathcompetence/${item.id}`);
      const listCompetencies = newLpCompetencies?.filter(
        (el) => el?.id !== item.id
      );
      setLpCompetencies(listCompetencies);
      setNewLpCompetencies(listCompetencies);
    } catch (error) {
      console.error('Error saving:', error);
    }
  };

  return (
    <div
      className={`flex h-full max-h-[calc(100vh_-_80px)] flex-col overflow-y-auto ${
        isMobile && 'min-h-[calc(100vh_-_126px)]'
      }`}
    >
      {learningpath?.lpPublicationStatus === 'disabled' && (
        <div className="mx-auto w-full max-w-[824px] px-3">
          <div className="my-3 flex flex-col gap-3 rounded-lg bg-error-01 p-4">
            <h3 className="text-base font-semibold text-error-05">
              {t('pages.learning_path.message_title_deactivate')}
            </h3>
            <p className="text-sm text-error-05">
              {t('pages.learning_path.message_deactivate')}
            </p>
            <div>
              <div className="group relative inline-block">
                <Button
                  variant="ghost"
                  onClick={() => handleActivate()}
                  className={`border !bg-error-04 !text-error-01 
                           ${!isOwner && 'opacity-25'}
                            `}
                  disabled={!isOwner}
                >
                  {t('pages.create_edit_course.activate_button')}
                </Button>
                {!isOwner && (
                  <div className="absolute left-0 top-[110%] z-[10] hidden w-[300px] rounded-lg bg-white p-3 text-xs text-grey-08 shadow-[0_0_5px_-1px_rgba(0,0,0,0.3)] group-hover:flex">
                    {t('pages.create_edit_course.message_not_owner')}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      <div>
        <div className="mb-5 flex flex-col gap-3 px-5">
          <div className="mb-2 mt-5 text-sm text-black">
            <Label htmlFor="name">
              <p className="mb-2 text-sm font-normal text-black">
                {t('pages.learning_path.name_change')}
              </p>
            </Label>
            <InputText
              id="name"
              onChange={(e) => changeName(e.currentTarget.value)}
              value={homeName}
              className="w-full"
              onBlur={save}
            />
          </div>
          <div className="mb-2 mt-6 text-sm text-black">
            <Label htmlFor="description">
              <p className="mb-2 text-sm font-normal text-black">
                {t('pages.learning_path.description_change')}
              </p>
            </Label>
            <InputTextarea
              id="description"
              limit={300}
              onChange={(e) => changeDescription(e.target.value)}
              value={homeDescription}
              onBlur={save}
            />
          </div>

          <div className="mx-auto w-full">
            {loadingImgSpinner && (
              <div className="mx-auto flex justify-center p-10">
                <TEOLoader
                  loaderSrc="/images/TEOloader.gif"
                  className="w-[100px]"
                />
              </div>
            )}
            <UploadMainImage
              setUploadLogo={changeImage}
              uploadLogo={homeImage}
              courseImg={true}
              learningPathHomeImg={true}
              setLoadingImgSpinner={setLoadingImgSpinner}
              loadingImgSpinner={loadingImgSpinner}
            />
          </div>
        </div>
        <div className="border-t px-5 py-5">
          <h4 className="mb-1 text-base">
            {t('pages.learning_path.settings_label')}
          </h4>
          <div className=" mt-5 flex flex-col">
            <label className="mb-1 block text-sm font-semibold text-black">
              {t(`pages.create_publication.template_lang_lp`)}
            </label>
            <div id="templateLang">
              <InputSelect
                onChange={(lang) => {
                  setSelectLang(lang);
                  const changes = {};
                  if (lang?.value !== learningpath?.lang) {
                    changes.lang = lang?.value;
                    setLearningpath((prev) => ({
                      ...prev,
                      lang: lang?.value,
                    }));
                    const promises = [];
                    if (!isEmpty(changes)) {
                      promises.push(
                        axios.put(
                          `${config.backend}/learningpath/${learningpathId}`,
                          changes
                        )
                      );
                    }
                  }
                }}
                value={
                  selectLang
                    ? selectLang
                    : newLangData?.find((option) => option?.value === authLang)
                }
                options={[
                  {
                    name: t('pages.create_library.all'),
                    value: 'null',
                    id: 1234567,
                    flag: false,
                  },
                  ...newLangData,
                ]}
              >
                {(options) => (
                  <span>
                    {options?.value != 'null' ? options?.name : options?.name}
                  </span>
                )}
              </InputSelect>
            </div>
          </div>
          <div className=" mt-5 flex flex-col">
            <label className="mb-1 block text-sm font-semibold text-black">
              {t('pages.learning_path.settings.category')}
            </label>
            <InputSelect
              value={lpCategory}
              onChange={(value) => {
                handleCategory(value);
              }}
              options={categories}
              placeholder={t('pages.learning_path.settings.not_specified')}
            >
              {(options) => {
                const Icon = categoriesIcon(options.icon);
                return (
                  <span className="flex items-center">
                    {options && (
                      <>
                        <Icon className="mr-2 h-5 w-5 min-w-5  text-[#231F20]" />
                        {options?.label}
                      </>
                    )}
                  </span>
                );
              }}
            </InputSelect>
          </div>

          <div className="">
            <Label htmlFor="categories">
              <p className="mt-6 text-sm font-semibold">
                {t('pages.create_edit_course.tags')}
              </p>
            </Label>
            <div className=" flex items-center">
              <Tag className="mr-2 h-[17.5px] w-[17.5px] text-[#6d6d72]" />

              <div
                className="w-full"
                onSelect={() => !isloadedAITags && setIsloadedAITags(true)}
              >
                <PaginationMultiselectWithTags
                  value={selectTags}
                  onChange={(value) => {
                    setSelectTags(value);
                  }}
                  filter={filter}
                  setFilter={setFilter}
                  keyFn={(row) => row?.tag}
                  fetch={useGet.bind(
                    null,
                    `/tags?lang=${i18n.language}&organisationId=${auth?.user?.activeOrganisationId},OR,organisationId=null`
                  )}
                  defaultSort={[{ id: 'tag', desc: false }]}
                  render={(row) => {
                    return row?.tag;
                  }}
                  customStyles="bg-white"
                  addTagLabel={`${filter} ${t(
                    'pages.create_edit_course.addTagLabel'
                  )}`}
                  handleAddTag={handleAddTag}
                  placeholder={
                    selectTags?.length > 0
                      ? ''
                      : t('pages.create_edit_course.tags_placeholder')
                  }
                />
              </div>
            </div>
            {isloadedAITags && (
              <div className="">
                <AITags
                  tags={aiTags}
                  loading={aiTagsLoading}
                  setTags={setAiTags}
                  setSelectTags={setSelectTags}
                />
              </div>
            )}
          </div>

          <div className="mt-5 flex flex-col">
            <label className="mb-1 block text-sm font-semibold text-black">
              {t('pages.learning_path.settings.order')}
            </label>
            <RadioGroupTab
              onChange={(value) => {
                setTypeOfOrder(value);
                setLearningpath((prev) => ({ ...prev, order: value }));
                axios.put(`${config.backend}/learningpath/${learningpathId}`, {
                  order: value,
                });
              }}
              value={typeOfOrder}
              classBody="bg-grey-02 !p-0.5"
            >
              <div className="flex w-full flex-row gap-0.5">
                <div className={`basis-1/2 rounded-lg`}>
                  <RadioGroupTab.Option
                    value="fixed"
                    hiddenRadio
                    checkedClassName="bg-primary-04"
                    className="!block !p-2 text-center"
                  >
                    <span className="text-sm font-medium text-grey-09">
                      {t('pages.learning_path.settings.fixed')}
                    </span>
                  </RadioGroupTab.Option>
                </div>
                <div className={`basis-1/2 rounded-lg `}>
                  <RadioGroupTab.Option
                    value="flexible"
                    hiddenRadio
                    checkedClassName="bg-primary-04"
                    className="!block !p-2 text-center"
                  >
                    <span className="text-sm font-medium text-grey-09">
                      {t('pages.learning_path.settings.flexible')}
                    </span>
                  </RadioGroupTab.Option>
                </div>
              </div>
            </RadioGroupTab>
          </div>
        </div>
      </div>
      {addCompetency && (
        <AddCompetenceLP
          setIsOpenCompetencies={setAddCompetency}
          isOpenCompetencies={addCompetency}
          node={learningpath}
          setNewCompetencies={setNewLpCompetencies}
          newCompetencies={newLpCompetencies}
          competencies={lpCompetencies}
          refetchCompetencies={refetchCompetencies}
          training={false}
        />
      )}
    </div>
  );
};

export default HomeSettings;
